import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Zühlke PrimeNG theme preset.
 *
 * Extends the Aura base preset and:
 * - Maps `semantic.primary` to the Zühlke purple scale (`zuehlkePurple`)
 * - Maps `semantic.colorScheme` surfaces + text to `zuehlkeSurface` (aligned with `--zuehlke-*` in styles.scss)
 *   so **secondary** buttons and neutral UI match the app shell (not Aura’s default slate)
 * - **Badge `severity="secondary"`** uses translucent black (light) / translucent white (dark) instead of solid
 *   surface greys, so counts stay legible on tinted or coloured backgrounds (e.g. Smart Discovery evidence strip)
 * - Rewires Button **help** from Aura’s generic `purple.*` to `zuehlkePurple` so it matches brand primary
 * - **Button** `root` uses `var(--app-field-radius)` and `var(--app-btn-padding-*)` to match global `.app-btn`
 * - **Spacing / radius** — `primitive.borderRadius` and shared `semantic` spacing (focus ring, form fields,
 *   lists, navigation, overlays) reference `var(--space-*)`, `var(--space-focus-ring-*)`, and
 *   `var(--app-field-radius)` (default square-ish fields; pill tabs use `--app-control-pill-radius` in app SCSS).
 *
 * Success / warn / danger / info keep Aura’s semantic greens, oranges, reds, and sky blues for clarity.
 *
 * Run `pnpm start` to see changes live.
 */
export const zuehlkePreset = definePreset(Aura, {
  primitive: {
    zuehlkePurple: {
      50: '#f7f0f8',
      100: '#ece1ed',
      200: '#d4b9d6',
      300: '#b58fb8',
      400: '#9669a0',
      500: '#834f87',
      600: '#6c3f70',
      700: '#57325b',
      800: '#3f2442',
      900: '#2a172c',
      950: '#180d1a',
    },
    /** Neutral scale aligned with `styles.scss` body bg, borders, body/secondary text. */
    zuehlkeSurface: {
      0: '#ffffff',
      50: '#fafafa',
      100: '#f0f0f0',
      200: '#e4e4e4',
      300: '#cccccc',
      400: '#a3a3a3',
      500: '#737373',
      600: '#5c5c5c',
      700: '#333333',
      800: '#1f1f1f',
      900: '#111111',
      950: '#0a0a0a',
    },
    /** Aligned to `app-spacing.scss` steps (2/4/8/12px ≈ xs–xl). */
    borderRadius: {
      none: '0',
      xs: 'var(--space-1)',
      sm: 'var(--space-2)',
      md: 'var(--space-3)',
      lg: 'var(--space-3)',
      xl: 'var(--space-4)',
    },
  },
  semantic: {
    primary: {
      50: '{zuehlkePurple.50}',
      100: '{zuehlkePurple.100}',
      200: '{zuehlkePurple.200}',
      300: '{zuehlkePurple.300}',
      400: '{zuehlkePurple.400}',
      500: '{zuehlkePurple.500}',
      600: '{zuehlkePurple.600}',
      700: '{zuehlkePurple.700}',
      800: '{zuehlkePurple.800}',
      900: '{zuehlkePurple.900}',
      950: '{zuehlkePurple.950}',
    },
    fontFamily: "'Lato', sans-serif",
    focusRing: {
      width: 'var(--space-focus-ring-width)',
      style: 'solid',
      color: '{primary.color}',
      offset: 'var(--space-focus-ring-offset)',
      shadow: 'none',
    },
    anchorGutter: 'var(--space-1)',
    mask: {
      background: 'var(--tone-overlay-modal-backdrop)',
      transition: {
        duration: '0.2s',
      },
    },
    formField: {
      paddingX: 'var(--space-4)',
      paddingY: 'var(--space-3)',
      sm: {
        fontSize: '0.875rem',
        paddingX: 'var(--space-4)',
        paddingY: 'var(--space-3)',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: 'var(--space-5)',
        paddingY: 'var(--space-3)',
      },
      borderRadius: 'var(--app-field-radius)',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none',
      },
      transitionDuration: '{transition.duration}',
    },
    list: {
      padding: 'var(--space-2) var(--space-2)',
      gap: 'var(--space-1)',
      header: {
        padding: 'var(--space-3) var(--space-5) var(--space-2) var(--space-5)',
      },
      option: {
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: '{border.radius.sm}',
      },
      optionGroup: {
        padding: 'var(--space-3) var(--space-4)',
        fontWeight: '600',
      },
    },
    content: {
      borderRadius: '{border.radius.md}',
    },
    navigation: {
      list: {
        padding: 'var(--space-2) var(--space-2)',
        gap: 'var(--space-1)',
      },
      item: {
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: '{border.radius.sm}',
        gap: 'var(--space-3)',
      },
      submenuLabel: {
        padding: 'var(--space-3) var(--space-4)',
        fontWeight: '600',
      },
      submenuIcon: {
        size: '0.875rem',
      },
    },
    overlay: {
      popover: {
        borderRadius: '{border.radius.md}',
        padding: 'var(--space-4)',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      modal: {
        borderRadius: '0.75rem',
        padding: 'var(--space-6)',
        shadow: 'var(--tone-shadow-panel)',
      },
      navigation: {
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      select: {
        borderRadius: '{border.radius.md}',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: '{zuehlkeSurface.0}',
          50: '{zuehlkeSurface.50}',
          100: '{zuehlkeSurface.100}',
          200: '{zuehlkeSurface.200}',
          300: '{zuehlkeSurface.300}',
          400: '{zuehlkeSurface.400}',
          500: '{zuehlkeSurface.500}',
          600: '{zuehlkeSurface.600}',
          700: '{zuehlkeSurface.700}',
          800: '{zuehlkeSurface.800}',
          900: '{zuehlkeSurface.900}',
          950: '{zuehlkeSurface.950}',
        },
        text: {
          color: '{zuehlkeSurface.900}',
          hoverColor: '{zuehlkeSurface.950}',
          mutedColor: 'var(--tone-text-subtle)',
          hoverMutedColor: '{zuehlkeSurface.600}',
        },
        content: {
          background: '{zuehlkeSurface.0}',
          hoverBackground: '{zuehlkeSurface.50}',
          borderColor: '{zuehlkeSurface.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
      },
    },
  },
  components: {
    dialog: {
      root: {
        borderRadius: '0.75rem',
        shadow: 'var(--tone-shadow-panel)',
      },
      header: {
        padding: 'var(--space-5) var(--space-7)',
      },
      title: {
        fontSize: 'var(--type-title-sm-font-size)',
        fontWeight: '600',
      },
      content: {
        padding: 'var(--space-6) var(--space-7)',
      },
    },
    badge: {
      colorScheme: {
        light: {
          secondary: {
            background: 'rgba(0, 0, 0, 0.10)',
            color: 'rgba(0, 0, 0, 0.78)',
          },
        },
      },
    },
    button: {
      root: {
        borderRadius: 'var(--app-field-radius)',
        paddingY: 'var(--app-btn-padding-y)',
        paddingX: 'var(--app-btn-padding-x)',
      },
      colorScheme: {
        light: {
          root: {
            help: {
              background: '{zuehlkePurple.500}',
              hoverBackground: '{zuehlkePurple.600}',
              activeBackground: '{zuehlkePurple.700}',
              borderColor: '{zuehlkePurple.500}',
              hoverBorderColor: '{zuehlkePurple.600}',
              activeBorderColor: '{zuehlkePurple.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: { color: '{zuehlkePurple.500}', shadow: 'none' },
            },
          },
          outlined: {
            help: {
              hoverBackground: '{zuehlkePurple.50}',
              activeBackground: '{zuehlkePurple.100}',
              borderColor: '{zuehlkePurple.200}',
              color: '{zuehlkePurple.600}',
            },
          },
          text: {
            help: {
              hoverBackground: '{zuehlkePurple.50}',
              activeBackground: '{zuehlkePurple.100}',
              color: '{zuehlkePurple.600}',
            },
          },
        },
      },
    },
  },
});
